import { useCallback, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Table,
  Group,
  Pagination,
  Loader,
  Badge,
  Paper,
} from "@mantine/core";
import { DatePickerInput, type DatesRangeValue } from "@mantine/dates";
import { useMyActivity } from "../hooks/useMyActivity";
import {
  getLabTodayDateOnly,
  parseDateOnly,
  type DateOnlyString,
} from "../utils/dateOnly";
import { LAB_TIME_ZONE } from "../utils/labTime";

const ACTION_COLORS: Record<string, string> = {
  view: "blue",
  create: "green",
  update: "yellow",
  delete: "red",
  login: "teal",
  logout: "gray",
  export: "violet",
  print: "indigo",
  page_visit: "cyan",
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    timeZone: LAB_TIME_ZONE,
  });
}

type ActivityDateRange = [DateOnlyString | null, DateOnlyString | null];

export default function MyActivityPage() {
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState<ActivityDateRange>([null, null]);
  const maximumDate = useMemo(() => getLabTodayDateOnly(), []);

  const filters = useMemo(
    () => ({
      page,
      limit: 50,
      ...(dateRange[0] && { startDate: dateRange[0] }),
      ...(dateRange[1] && { endDate: dateRange[1] }),
    }),
    [page, dateRange]
  );

  const { data: result, isLoading } = useMyActivity(filters);
  const logs = useMemo(() => result?.data ?? [], [result]);
  const pagination = useMemo(() => result?.pagination, [result]);

  const handleDateRangeChange = useCallback((value: DatesRangeValue) => {
    const startDate = parseDateOnly(value[0]);
    const endDate = parseDateOnly(value[1]);
    const hasInvalidValue =
      (value[0] !== null && !startDate) || (value[1] !== null && !endDate);

    setDateRange(
      hasInvalidValue ? [null, null] : [startDate ?? null, endDate ?? null]
    );
    setPage(1);
  }, []);

  const handlePageChange = useCallback((value: number) => {
    setPage(value);
  }, []);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>My Activity</Title>
          <Text size="sm" c="dimmed" mt="xs">
            A history of pages you have visited and records you have accessed
          </Text>
        </div>

        <Paper p="md" withBorder>
          <Group>
            <DatePickerInput
              type="range"
              label="Date Range"
              placeholder="Filter by date range"
              value={dateRange}
              onChange={handleDateRangeChange}
              maxDate={maximumDate}
              clearable
            />
          </Group>
        </Paper>

        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Timestamp</Table.Th>
                  <Table.Th>Action</Table.Th>
                  <Table.Th>Resource Type</Table.Th>
                  <Table.Th>Resource ID</Table.Th>
                  <Table.Th>URL</Table.Th>
                  <Table.Th>IP Address</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {logs.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text ta="center" c="dimmed" py="xl">
                        No activity found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  logs.map((log) => (
                    <Table.Tr key={log.id}>
                      <Table.Td>
                        <Text size="sm">{formatDate(log.createdAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={ACTION_COLORS[log.action] || "gray"}
                          variant="light"
                          size="sm"
                        >
                          {log.action}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" tt="capitalize">
                          {log.resourceType.replace(/_/g, " ")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace" c="dimmed">
                          {log.resourceId
                            ? `${log.resourceId.substring(0, 8)}...`
                            : "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {log.url ? (
                          <Text size="xs" ff="monospace" title={log.url}>
                            {log.url.length > 60
                              ? `${log.url.substring(0, 60)}…`
                              : log.url}
                          </Text>
                        ) : (
                          <Text size="xs" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {log.ipAddress}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>

            {pagination && pagination.totalPages > 1 && (
              <Group justify="center">
                <Pagination
                  total={pagination.totalPages}
                  value={page}
                  onChange={handlePageChange}
                />
              </Group>
            )}

            {pagination && (
              <Text size="sm" c="dimmed" ta="center">
                Showing {logs.length} of {pagination.total} activity entries
              </Text>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}
